import { useTranslation } from 'react-i18next';
import type { SendType } from '../../types/climb-log';

interface SendAttemptsLabelProps {
  sendType: SendType | null | undefined;
  attemptsCount: number | null | undefined;
  /**
   * Names the worked case ("Ripetuto (4)") instead of showing the bare count.
   * For lists that stand on their own — a crag's ascents, say — where a lone
   * number has no column heading to explain it.
   */
  verbose?: boolean;
}

/**
 * How a send was done, in one word or one number: the style when the climber
 * named one, the number of goes when they worked it.
 *
 * Shared by the sends table on a climb profile, the sends drawer on a crag's
 * topo and the crag's own ascents tab — the same answer to "how did they do
 * it", so it reads the same everywhere.
 */
export function SendAttemptsLabel({ sendType, attemptsCount, verbose = false }: SendAttemptsLabelProps) {
  const { t } = useTranslation();

  switch (sendType) {
    case 'on_sight':
      return <span className="text-emerald-600 font-medium">{t('climbLog.onSight')}</span>;
    case 'flash':
      return <span className="text-green-600 font-medium">{t('climbLog.flash')}</span>;
    case 'nd':
      return <span className="text-muted-foreground">{t('climbLog.nd')}</span>;
    // 'worked' and an unnamed style both come down to the count of goes.
    default:
      if (verbose) {
        return (
          <span className="text-muted-foreground">
            {t('climbLog.worked')}
            {attemptsCount != null && ` (${attemptsCount})`}
          </span>
        );
      }
      return <span className="text-muted-foreground">{attemptsCount ?? '-'}</span>;
  }
}
